import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { OperatorScanPanel } from './OperatorScanPanel';
import { orderScanEntered, selectOrderElsewhere } from '../../../features/operator/operatorSlice';
import { findOrder, orderScanGate } from '../../../features/operator/operatorThunks';
import { OPERATOR_FLOW, OPERATOR_FLOW_PATHS } from '../../../features/operator/operatorConstants';
import {
  orderCardTitle,
  orderNextStepText,
  orderOpenLink,
  selectCourierSendReady,
  selectEnabledFlows,
} from '../../../features/operator/operatorUtils';
import { wmsOrderStateLabel } from '../../../utils/inventoryLabels';

/** A scanned order that goes somewhere else: its state, and a way there when the operator works it. */
const OrderScanResult = ({ result }) => {
  const navigate = useNavigate();
  const flows = useSelector(selectEnabledFlows);
  const courierSend = useSelector(selectCourierSendReady);
  const link = orderOpenLink(result, flows, { courierSend });

  return (
    <div
      data-testid="order-scan-result"
      data-order-id={result.order_id}
      data-next-flow={result.next_flow ?? ''}
      data-waiting-stock={String(Boolean(result.waiting_stock))}
      className="space-y-3 rounded-2xl border border-border bg-white p-4 shadow-sm dark:bg-[#1a1a1a]"
    >
      <div>
        <p data-testid="order-scan-title" className="break-words text-base font-bold text-ninja-dark dark:text-white">
          {orderCardTitle(result)}
        </p>
        <p className="text-sm text-text-secondary">
          {[wmsOrderStateLabel(result.wms_state), result.company_name].filter(Boolean).join(' · ')}
        </p>
      </div>
      {link ? (
        <Button variant="outline" size="lg" fullWidth onClick={() => navigate(link.to)} data-testid="order-scan-open">
          {link.label}
        </Button>
      ) : (
        <p className="text-sm font-semibold text-ninja-dark dark:text-white">{orderNextStepText(result)}</p>
      )}
    </div>
  );
};

/** What the scan of each queue reads under its prompt. */
const ORDER_SCAN_HINTS = {
  [OPERATOR_FLOW.PICKING]: 'Su referencia, su número o la etiqueta del envío. O toca una de la lista.',
  [OPERATOR_FLOW.PACKING]: 'Escanea el código de orden o toca una de la lista.',
};

/**
 * The scan of an order queue: the order scanned opens here when it goes to
 * this flow. When it goes somewhere else the band says where, and its card
 * stays below the panel. Opens nothing once the queue is off screen.
 *
 * @param {object} props
 * @param {'picking'|'packing'} props.flow
 */
export const OrderQueueScan = ({ flow }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const orderScan = useSelector((state) => state.operator.orderScan);
  const elsewhere = useSelector(selectOrderElsewhere(flow));
  const gate = orderScanGate(orderScan, flow);

  useEffect(() => {
    dispatch(orderScanEntered());
  }, [dispatch]);

  const openScanned = async (code, source, alternatives) => {
    const action = await dispatch(findOrder({ code, source, flow, alternatives }));
    if (findOrder.fulfilled.match(action) && !action.meta.offScreenTask && action.payload.next_flow === flow) {
      navigate(`${OPERATOR_FLOW_PATHS[flow]}/${action.payload.order_id}`);
    }
  };

  return (
    <div className="space-y-3">
      <OperatorScanPanel
        prompt="Escanea la orden"
        hint={ORDER_SCAN_HINTS[flow]}
        accepting={!gate.busy}
        lastCode={gate.lastCode}
        holdReads={false}
        busyLabel="Buscando…"
        onCode={openScanned}
      />
      {elsewhere && <OrderScanResult result={elsewhere} />}
    </div>
  );
};
